import { Box, Typography, colors, IconButton, Stack } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { GameContext } from '../../../contexts/GameContext';
import ScoreData from './ScoreData';
import SocketContext from '../../../contexts/SocketContext';

const pulseKeyframes = {
    '@keyframes pulse': {
        '0%, 100%': { opacity: 0.4 },
        '50%': { opacity: 1 },
    },
};

const arrowSx = {
    position: 'absolute',
    top: -70,
    color: 'white',
    fontSize: 60,
    cursor: 'pointer',
    animation: 'pulse 2s ease-in-out infinite',
    zIndex: 2,
    '&:hover': { opacity: 1 },
} as const;

const GameFooter = () => {
    const gameContext = useContext(GameContext);
    const { socket } = useContext(SocketContext).SocketState;
    const [tokenCount, setTokenCount] = useState(0);

    useEffect(() => {
        if (gameContext.players.length === 0) return;
        const me = gameContext.players.find((p) => p.playerId === socket?.id);
        if (me) setTokenCount(me.tokenCount);
    }, [gameContext.players]);

    // Drag to scroll
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const startXRef = useRef(0);
    const scrollLeftRef = useRef(0);
    const [scrollRatio, setScrollRatio] = useState(0);
    const [thumbRatio, setThumbRatio] = useState(1);
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

    const updateScrollIndicators = useCallback(() => {
        const el = scrollRef.current;
        if (!el) return;
        setCanScrollLeft(el.scrollLeft > 1);
        setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 1);
        const maxScroll = el.scrollWidth - el.clientWidth;
        setScrollRatio(maxScroll > 0 ? el.scrollLeft / maxScroll : 0);
        setThumbRatio(el.scrollWidth > 0 ? el.clientWidth / el.scrollWidth : 1);
        setIsScrolling(true);
    }, []);

    useEffect(() => {
        updateScrollIndicators();
        const el = scrollRef.current;
        if (!el) return;
        const observer = new ResizeObserver(updateScrollIndicators);
        observer.observe(el);
        return () => observer.disconnect();
    }, [updateScrollIndicators, gameContext.players]);

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsDragging(true);
        clearTimeout(scrollTimeoutRef.current);
        setIsScrolling(true);
        startXRef.current = e.pageX - (scrollRef.current?.offsetLeft || 0);
        scrollLeftRef.current = scrollRef.current?.scrollLeft || 0;
    };

    const handleMouseLeave = () => {
        setIsDragging(false);
        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 300);
    };
    const handleMouseUp = () => {
        setIsDragging(false);
        scrollTimeoutRef.current = setTimeout(() => setIsScrolling(false), 300);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (!isDragging) return;
        e.preventDefault();
        const x = e.pageX - (scrollRef.current?.offsetLeft || 0);
        const walk = x - startXRef.current;
        if (scrollRef.current) {
            scrollRef.current.scrollLeft = scrollLeftRef.current - walk;
        }
    };

    const scrollByDirection = (direction: 'left' | 'right') => {
        const el = scrollRef.current;
        if (!el) return;
        const amount = el.clientWidth * 0.8;
        el.scrollBy({ left: direction === 'left' ? -amount : amount, behavior: 'smooth' });
    };

    return (
        <Box
            component="footer"
            sx={{
                ...pulseKeyframes,
                position: 'fixed',
                bottom: 0,
                left: 0,
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                gap: 3,
                px: 2,
                py: 2,
            }}
        >
            {/* Players list - scrollable */}
            <Box sx={{ position: 'relative', flex: 1, minWidth: 0, overflow: 'hidden' }}>
                {canScrollLeft && (
                    <ChevronLeftIcon sx={{ ...arrowSx, left: -4 }} onClick={() => scrollByDirection('left')} />
                )}
                {canScrollRight && (
                    <ChevronRightIcon sx={{ ...arrowSx, right: -4 }} onClick={() => scrollByDirection('right')} />
                )}
                <Stack
                    direction="row"
                    spacing={0}
                    ref={scrollRef}
                    onMouseDown={handleMouseDown}
                    onMouseLeave={handleMouseLeave}
                    onMouseUp={handleMouseUp}
                    onMouseMove={handleMouseMove}
                    onScroll={updateScrollIndicators}
                    sx={{
                        overflowX: 'auto',
                        overflowY: 'hidden',
                        cursor: isDragging ? 'grabbing' : 'grab',
                        scrollbarWidth: 'none',
                        '&::-webkit-scrollbar': { display: 'none' },
                    }}
                >
                    {gameContext.players.map((player, index) => (
                        <Stack
                            key={`${player.playerName}${index}`}
                            alignItems="center"
                            spacing={0.75}
                            sx={{
                                minWidth: 120,
                                maxWidth: 140,
                                flexShrink: 0,
                                py: 0.5,
                            }}
                        >
                            <Typography
                                align="center"
                                noWrap
                                sx={{
                                    fontSize: { xs: '1rem', sm: '1.15rem', md: '1.25rem' },
                                    maxWidth: '100%',
                                    color: player.playerColor || 'white',
                                }}
                            >
                                {player.playerName}
                            </Typography>

                            <ScoreData
                                quantity={player.cards.length}
                                background={'url(/images/cards/cardData.png)'}
                                textVariant={'h6'}
                                iconSize={42}
                            />

                            <ScoreData
                                quantity={player.tokenCount}
                                background={'url(/images/cards/jeton.png)'}
                                textVariant={'h6'}
                                iconSize={42}
                            />

                            {gameContext.isCreator && (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            if (player.tokenCount > 0) {
                                                gameContext.getClient().UpdatePlayerTokens(player.playerId, -1);
                                            }
                                        }}
                                        disabled={player.tokenCount <= 0}
                                        sx={{
                                            color: 'white',
                                            bgcolor: 'rgba(255,255,255,0.15)',
                                            width: 36,
                                            height: 36,
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                        }}
                                    >
                                        <RemoveIcon sx={{ fontSize: 22 }} />
                                    </IconButton>
                                    <IconButton
                                        size="small"
                                        onClick={() => {
                                            gameContext.getClient().UpdatePlayerTokens(player.playerId, 1);
                                        }}
                                        sx={{
                                            color: 'white',
                                            bgcolor: 'rgba(255,255,255,0.15)',
                                            width: 36,
                                            height: 36,
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                                        }}
                                    >
                                        <AddIcon sx={{ fontSize: 22 }} />
                                    </IconButton>
                                </Stack>
                            )}
                        </Stack>
                    ))}
                </Stack>
                {thumbRatio < 1 && (
                    <Box sx={{
                        width: '80%',
                        height: 4,
                        borderRadius: 2,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                        margin: '8px auto 0',
                        position: 'relative',
                        overflow: 'hidden',
                        opacity: isScrolling ? 1 : 0.3,
                        transition: isScrolling ? 'opacity 0s' : 'opacity 2s ease-out',
                    }}>
                        <Box sx={{
                            position: 'absolute',
                            top: 0,
                            left: `${scrollRatio * (1 - thumbRatio) * 100}%`,
                            width: `${thumbRatio * 100}%`,
                            height: '100%',
                            borderRadius: 2,
                            backgroundColor: isScrolling ? 'rgba(255,255,255,0.2)' : 'rgba(255,255,255,0.06)',
                            transition: isScrolling
                                ? 'left 0.05s ease-out, background-color 0s'
                                : 'left 0.05s ease-out, background-color 2s ease-out',
                        }} />
                    </Box>
                )}
            </Box>

            {/* My token count */}
            <Box sx={{ flexShrink: 0, pl: 3, pr: 1 }}>
                <ScoreData
                    quantity={tokenCount}
                    background={'url(/images/cards/jeton.png)'}
                    textVariant={'h4'}
                    iconSize={50}
                />
            </Box>
        </Box>
    );
};

export default GameFooter;
